import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, debounce, Dialog, Grid, InputAdornment, Snackbar, Stack, Typography } from "@mui/material";

import PageHeader from "components/Header/Page";
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { usePepwiseAuthController } from "Context/contexts/AuthContext";
import { getProgramList, getProgramSectorList, getProjectListByProgram } from "service/service";
import TitleStatus from "components/Widgets/TitleStatus";
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import Loader from "components/Loader/loader";
import { DataPipelineModal } from "./modal-component";
import { CustomizedDataGrid } from "components/customized_data_grid";
import RowTypography from "components/Table/RowTypography";
import SectorFilter from "components/Dropdown/SectorFilter";
import { checkRole } from "utils/roleUtils";
import CustomTextField from "components/Inputs/CustomTextField";
import { Search } from "@mui/icons-material";
import PageWrapper from "components/Common/PageWraper";
import colors from "assets/theme/base/colors";
import { getBackgroundSx, getSearchInputLabelProps, searchInputSx, setRdgTableHeight } from "./DataPipelineHelper";
import { ipDelayedStartFormatter, ipLongRunningFormatter, jobsFailedFormatter, jobsSuccessFormatter, projectLinkFormatter, projectSlaStatusFormatter, slaDateTimeFormatter } from "./DataPipelineFormatter";

const ProgramsDashboard = () => {
	const [openModal, setOpenModal] = useState<any>({ isOpen: false });
	const [programList, setProgramList] = useState<any>([]);
	const [loading, setLoading] = useState(false);
	const [tableLoading, setTableLoading] = useState(false);
	const [currentProgram, setCurrentProgram] = useState<any>({});
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [projectList, setProjectList] = useState<any>([]);
	const page = 1;
	const pageSize = 100;
	const [controller] = usePepwiseAuthController();
	const { role } = controller;
	const defaultColumnNames = ['Sectors', 'Project', 'Delayed Start', 'Long Running', 'Failed', 'Success', 'SLA Status', 'SLA Time (CST)'];
	const filterData: any[] = [];
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const searchDetails: any[] = [];
	const filterInfo: any[] = [];	
	const [sectorList, setSectorList] = useState<any[]>([]);
	const [sectorName, setSectorName] = useState('all');
	const [searchProgram, setSearchProgram] = useState('');
	const [toast, setToast] = useState<any>({
		open: false,
		message: ''
	});
	const hiddenSector = "SODASTREAM";
	const filteredSectorList = sectorList.filter(sector => sector !== hiddenSector);
	const messagesEndRef = useRef<null | HTMLDivElement>(null);
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};
	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
	const getColumns = () => {
		return [
			{
				key: 'Sectors',
				name: 'Sectors',
				formatter(props: any) {
					const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
					if (sectorList && sectorList.length) {
						return sectorList.map((sector: string) => <RowTypography sx={{ backgroundColor: 'rgba(41, 155, 255, 0.15)', color: '#fff', padding: '4px 10px', marginRight: '5px', borderRadius: '20px' }} key={sector} title={sector}>{sector}</RowTypography>);
					} else {
						return '--'
					}
				},
				minWidth: 150,
				maxWidth: 250
			},
			{
				key: 'Project_Name',
				name: 'Project',
				formatter: projectLinkFormatter(currentProgram.Program_Name),
				minWidth: 200,
				maxWidth: 500
			},
			{
				key: 'IP_Delayed_Start',
				name: 'Delayed Start',
				formatter: ipDelayedStartFormatter(),
				minWidth: 100
			},
			{
				key: 'IP_Long_Running',
				name: 'Long Running',
				formatter: ipLongRunningFormatter(),
				minWidth: 100
			},
			{
				key: 'Failed',
				name: 'Failed',
				formatter: jobsFailedFormatter(),
				minWidth: 100
			},
			{
				key: 'Success',
				name: 'Success',
				formatter: jobsSuccessFormatter(),
				minWidth: 100
			},
			{
				key: 'SLA_Status',
				name: 'SLA Status',
				formatter: projectSlaStatusFormatter(),
				minWidth: 150
			},
			{
				key: 'SLA_Time',
				name: 'SLA Time (CST)',
				formatter: slaDateTimeFormatter(),
				minWidth: 150
			}
		];
	};

	const handleFilter = (e: any) => {
		console.log('FILTER', e);
	};

	const handleSearch = (e: any) => {
		console.log('FILTER', e);
	};

	const handleSorting = (sortingInfo: any) => {
		setSortingInfo(sortingInfo);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		getProjectsByProgram(currentProgram, value, pageSize);
	};

	const getToastData = (data: any) => {
		setToast({
			...toast,
			message: data.data.message
		});
	};

	const handleClose = () => {
		setToast({
			...toast,
			open: false
		});
	};

	const handlepageSizeChange = (e: any) => {
		getProjectsByProgram(currentProgram, page, e.target.value);
	};
	const onCreateProgramClick = useCallback(() => {
		setOpenModal({
			isOpen: true,
			type: 'Program',
			action: 'CREATE',
			data: ''
		});
	}, []);

	const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
	};
	const handleDetailsThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleProjectSLA = (programData: any, filterType: string) => {
		getProjectsByProgram(programData, 1, 100, filterType, getSectorId())
	}

	const onClickAddProject = (event: any, prgId: string) => {
		setOpenModal({
			...openModal,
			isOpen: true,
			type: 'Project',
			action: 'CREATE',
			data: { ProgramID: prgId }
		});
	}

	const handleEditClose = useCallback((closeType: any) => {
		setOpenModal({
			isOpen: false,
			closeType: closeType
		});
	}, []);

	const handleAction = (module: string, type: string, data: any, e: any) => {
		e.preventDefault();
		e.stopPropagation();
		handleDetailsThreeDotsClose(e);
		data = {
			...data,
			ProgramName: data.Program_Name,
			ProgramID: data.Program_id
		};
		switch (type) {
			case 'edit':
				setOpenModal({
					isOpen: true,
					type: module,
					action: 'EDIT',
					data: data
				});
				break;
			case 'delete':
				setOpenModal({
					isOpen: true,
					type: module,
					action: 'DELETE',
					data: data
				});
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/data-pipeline/programs')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}		
		if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
			setSectorList(JSON.parse(localStorage.getItem('filterSectors') ?? ''));
		} else {
			getSectorData();
		}
		const selSector = localStorage.getItem('selectedSector');
		if (selSector && selSector != 'all') {
			setSectorName(selSector)
		}
		getProgramData(getSectorId());
	}, []);

	useEffect(() => {
		if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
			getProgramData();
			setSearchProgram('');
		}
	}, [openModal]);	

	const handleSearchData = (searchText: string) => {
		setSearchProgram(searchText);
		debouncedFetchOption(searchText);
	};
	const debouncedFetchOption = useMemo(
		() =>
			debounce(async (searchText: string) => {
				try {
					getProgramData(getSectorId(), searchText);
					setProjectList([]);
				} catch (error) {
					console.error(error);
				}
			}, 500),
		[]
	);

	const handleSectorChange = (value: string) => {
		setSectorName(value);
		localStorage.setItem('selectedSector', value);		
		getProgramData(getSectorId());
		setProjectList([]);
	};

	const getSectorId = () => {
		let sectorId;
		let selectedSctr = localStorage.getItem('selectedSector')
		if (selectedSctr && selectedSctr != 'all') {
			const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
			if (sectorList) {
				sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
			}
		}
		return sectorId;
	}
	
	const getSectorData = async () => {
		let sectors: any = await getProgramSectorList();
		const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
		localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
		localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
		setSectorList(filterSectors);
	};

	const getProgramData = async (sectorId?: string, searchKeyword?: string) => {
		setLoading(true);
		let data: any = await getProgramList(sectorId, searchKeyword);
		setLoading(false);
		setProgramList(data.data.programData);
	};

	const getProjectsByProgram = async (data: any, page: number, pageSize: number, filterType?: string, sectorId?: string) => {
		page = page || 1;
		setTableLoading(true);
		setCurrentProgram(data);
		let result = await getProjectListByProgram(data.Program_id, page, pageSize, filterType, {}, '', '', sortingInfo, sectorId);
		setTimeout(() => {
			setRdgTableHeight(
				`project_list${data.Program_id}`,
				result.data.projectList.length
			);
		}, 100);
		setPaginationData(result.data.page);
		setTableLoading(false);
		const projData = result.data.projectList.map((list: any) => {
			list['activeRow'] = 0;
			const selectedSectors = list.Sectors && list.Sectors.split(', ');
			list['Sectors'] = selectedSectors && selectedSectors.length >= 10 ? ['All 10 Sectors'] : list.Sectors;
			return list;
		});
		setProjectList(projData);
		setTimeout(() => {
			scrollToBottom();
		}, 500);
	};

	return (
		<PageWrapper>
			<PageHeader title="Programs Dashboard" icon={DashboardIcon} lastSync={'Today'} isDataRefresh={true}>
				<CustomTextField
					className="search-prgm"
					placeholder="Search Programs"
					value={searchProgram}
					onChange={(e) => handleSearchData(e.target.value)}
					style={{ marginTop: '3px', backgroundColor: `${colors.search.background} !important`, borderRadius: '5px', zIndex: '99'}}
					sx={getBackgroundSx(colors)}
					InputLabelProps={getSearchInputLabelProps(colors)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search sx={{ color: 'white.main' }} />
							</InputAdornment>
						),
						sx: searchInputSx
					}}
				/>
				<SectorFilter 
					sectorName={sectorName}
					setSectorName={setSectorName}
					sectorOptionsList={filteredSectorList}
					handleChange={handleSectorChange} 
					boxProps={{
						sx: {minWidth: '200px', position: 'static'},
					}}/>

				{isAllowed && (
					<Button variant="pepwiseSecondary" onClick={onCreateProgramClick} sx={{ height: '43px'}}>
						CREATE PROGRAM
					</Button>
				)}
			</PageHeader>

			<Box p={3} mt={2}>
				{!loading ? (
					<Grid container spacing={2}>

						{programList.map((program: any) => (
							<Grid key={program.Program_id} container item xs={12} sm={6} md={4}>
								<TitleStatus
									title={program.Program_Name}
									programId={program.Program_id}
									isEditEnabled={isAllowed ? true : false}
									projectCount={program.ProjectCount}
									projectSLAs={[
										{ label: 'SLA Missed', count: program.SLA_Missed_Count, onBtnClick: (e) => handleProjectSLA(program, 'SLA Missed') },
										{ label: 'SLA Met', count: program.SLA_Met_Count, onBtnClick: (e) => handleProjectSLA(program, 'SLA Met') },
										{ label: 'In Progress', count: program.In_Progress_Count || 0, onBtnClick: (e) => handleProjectSLA(program, 'In Progress') },
										{ label: 'Scheduled', count: program.Scheduled_Count || 0, onBtnClick: (e) => handleProjectSLA(program, 'Scheduled') }
									]}
									statusCount={program.SLA_Missed_Count + program.SLA_Met_Count + (program?.Scheduled_Count ? program.Scheduled_Count : 0) + (program?.In_Progress_Count ? program.In_Progress_Count : 0)}
									infoTooltip={{
										dateValues: [
											{ label: 'Date Created', value: program?.Created_dt ?? '' },
											{ label: 'Last Updated', value: program?.Updated_dt ?? '' }
										]
									}}
									infoOptionMenu={{
										onClick: handleDetailsThreeDotsClick,
										onClose: handleDetailsThreeDotsClose,
										menuItems: [
											{
												key: 'edit',
												label: 'Edit',
												icon: <EditIcon />,
												onMenuClick: (e) => handleAction('Program', 'edit', program, e)
											},
											{
												key: 'delete',
												label: 'Delete',
												icon: <DeleteIcon />,
												onMenuClick: (e) => handleAction('Program', 'delete', program, e)
											}
										],
										sx: { width: '20px', height: '20px' }
									}}
									onClickAddProject={(e) => onClickAddProject(e, program.Program_id)}
								/>
							</Grid>
						))
						}

						{
							!programList.length && <Grid container item xs={12}>
									<Stack justifyContent="center" alignItems="center" height="60vh" width="100%">
									<Typography variant="pageTitle" color="dark.main">
										No data for the selected filters
									</Typography>
								</Stack>
							</Grid>
						}

					</Grid>
				) : (
					<Loader />
				)
				}
			</Box>

			{projectList.length > 0 && (
				<Box p={3} sx={{position: 'relative'}}>
					<Typography variant="overlayWidgetTitle" style={{ color: '#E5E5E5', position: 'absolute', top: 0 }} ref={messagesEndRef}>{currentProgram.Program_Name}</Typography>
						 {/* <Typography variant="subtitle2" component="span" style={{ marginLeft: '12px', color: '#F5F5F5', fontWeight: 400 }}>{currentProjectSLAFilter} Projects</Typography> */}
						 

					<CustomizedDataGrid
						tableColumns={getColumns()}
						tableData={projectList}
						tableType="programs-dashboard"
						defaultColumnNames={defaultColumnNames}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						filterDataOptions={filterData}
						searchDataValues={searchDetails}
						onFilterCheck={handleFilter}
						currFilter={filterInfo}
						onFilterSearch={handleSearch}
						page={page}
						pageSize={pageSize}
						paginationData={paginationData}
						listLength={projectList.length}
						handlepageSizeChange={handlepageSizeChange}
						handlePageChange={handlePageChange}
						isFetching={tableLoading}
						showExport={false}
						isDateRangeFilter={false}
						isDisabled={false}
						isShowMoreButton={false}
					/>
				</Box>
			)}
			{tableLoading && <Loader />}

			<Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
				<DataPipelineModal handleEditClose={handleEditClose} programData={openModal} 
					getToastData={getToastData}
				/>
			</Dialog>
			<Snackbar
				open={toast && openModal.message}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				autoHideDuration={6000}
				onClick={handleClose}
				message={openModal.message}
			/>
		</PageWrapper>
	);
}

export default ProgramsDashboard;